import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AlertBox from '../components/AlertBox';
import { API_BASE_URL } from '../config';

const VerifyPin: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();
  const email = (location.state as { email: string })?.email || '';
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, pin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid PIN');
      
      // On success, store the token
      localStorage.setItem('token', data.token);
      
      // Update the AuthContext state by checking auth status
      await checkAuthStatus();
      
      // Redirect to home
      navigate('/');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-yellow-50 flex items-center justify-center py-12">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-3xl font-elegant font-bold text-secondary-900 mb-2">
            Enter PIN
          </h2>
          <p className="text-secondary-600">
            Please enter the 6-digit PIN sent to <span className="font-semibold">{email}</span>
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && <AlertBox type="error" message={error} />}
            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-secondary-700 mb-2">
                6-digit PIN
              </label>
              <input
                type="text"
                id="pin"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors tracking-widest text-center text-lg font-mono"
                placeholder="Enter PIN"
                required
                pattern="\d{6}"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-400 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {isLoading ? 'Verifying...' : 'Verify PIN'}
            </button>
          </form>
        </div>
        <div className="text-center mt-6 space-y-2">
          <button
            onClick={() => navigate('/login', { state: { email } })}
            className="text-secondary-500 hover:text-primary-600 text-sm transition-colors"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyPin;
